import { NEW_MASCOTS } from '@daymade/common-lib/constants';
import clsx from 'clsx';
import AspectRatioContainer from 'components/about-us/AspectRatioContainer';
import AvatarSpeechBubble from 'components/dashboard/your-referrals/AvatarSpeechBubble';
import ImageWithFallback from 'components/ImageWithFallback';
import { AvatarOnBeachTowel } from 'components/referrals/2026/AvatarOnBeachTowel';
import { AvatarOnRubberRing } from 'components/referrals/2026/AvatarOnRubberRing';
import {
  FriendIconBubbleContent,
  FriendIconIndex,
  FriendIconPlayer,
} from 'components/referrals/2026/FriendIconShared';
import { WhiteSpeechBubble } from 'components/referrals/2026/WhiteSpeechBubble';
import { referralsFolder } from 'constants/Referrals';
import type { PlayerInvitee } from 'hooks/referrals/usePlayerInvitesAndInvitees';
import { useIsMobile } from 'hooks/ui/useIsMobile';
import { Fragment, useMemo } from 'react';
import CancellationFlowStep from './CancellationFlowStep';
import styles from './WinWithFriends.module.css';

const InviteeSlot = ({
  player,
  avatarUrl,
  index,
  tailPosition,
  placeholder,
  currentUser,
}: {
  player: FriendIconPlayer | null;
  avatarUrl?: string;
  index: FriendIconIndex;
  tailPosition: 'left-corner' | 'left-bottom';
  placeholder?: boolean;
  currentUser?: boolean;
}) => {
  return (
    <Fragment>
      <div
        className={clsx(
          styles.avatarContainer,
          styles[`avatarContainer${index}`],
        )}
      >
        <AvatarOnBeachTowel
          towelIndex={index}
          avatarUrl={avatarUrl ?? player?.avatarUrl ?? undefined}
          inactive={player?.status === 'Inactive'}
        />
      </div>
      {!placeholder &&
        (currentUser ? (
          <div
            className={clsx(
              styles.speechBubbleContainer,
              styles.currentUserRefereeSpeechBubble,
            )}
          >
            <AvatarSpeechBubble
              backgroundColour="coral"
              tailPosition="left-corner"
            >
              <p className={styles.avatarSpeechBubbleText}>
                <strong>You</strong>
              </p>
            </AvatarSpeechBubble>
          </div>
        ) : (
          <div
            className={clsx(
              'aboutUsTypographyReset',
              styles.speechBubbleContainer,
              styles[`speechBubbleContainer${index}`],
            )}
          >
            <WhiteSpeechBubble tailPosition={tailPosition} empty={!player}>
              <FriendIconBubbleContent
                player={player}
                hideStatus
                hackSmallerFont
              />
            </WhiteSpeechBubble>
          </div>
        ))}
    </Fragment>
  );
};

export const WinWithFriends = ({
  isReferrer,
  invitees,
  referrerAvatarUrl,
  currentUserAvatarUrl,
  returnToOverview,
  advanceCancellationFlow,
}: {
  isReferrer: boolean;
  invitees: PlayerInvitee[];
  referrerAvatarUrl?: string;
  currentUserAvatarUrl?: string;
  returnToOverview: () => void;
  advanceCancellationFlow: () => void;
}) => {
  const { isMobile } = useIsMobile();

  const randomMascotUrls = useMemo(
    () => ({
      '1': NEW_MASCOTS[1],
      '3': NEW_MASCOTS[3],
    }),
    [],
  );

  const visibleInvitees = invitees.slice(0, 3);
  const inviteeSlots = (['1', '2', '3'] as FriendIconIndex[]).map(
    (index, inviteeIndex) => {
      const invitee = visibleInvitees[inviteeIndex];
      return {
        index,
        player: invitee
          ? {
              avatarUrl: invitee.avatar ?? undefined,
              name: invitee.name,
              status: invitee.status,
              gracePeriodHoursRemaining: invitee.gracePeriodHoursRemaining,
            }
          : null,
      };
    },
  );

  const MESSAGE = isReferrer ? (
    <>
      You'll leave your <br /> group if you cancel
    </>
  ) : (
    <>
      You'll leave our <br /> group if you cancel
    </>
  );

  const SPEECH_BUBBLE_COLOR = isReferrer ? 'coral' : 'purple';

  const RUBBER_RING_COLOR = isReferrer ? 'red' : 'blue';
  return (
    <CancellationFlowStep
      icon="friends-icon-bg-green"
      heading={
        <>
          You're currently <br className="mobileOnly" />
          playing with friends
        </>
      }
      primaryCTAText={'Continue Playing with Friends'}
      primaryCTAOnClick={returnToOverview}
      secondaryCTAText="Proceed to cancel"
      secondaryCTAOnClick={advanceCancellationFlow}
      onGoBack={returnToOverview}
    >
      <p>
        If you cancel, you'll lose out on the chance to{' '}
        <strong>win rewards worth up to £5k</strong> whenever your{' '}
        {isReferrer ? 'friends' : 'friend'} {isReferrer ? 'win' : 'wins'} prizes
        in our weekly draws
      </p>
      <div className={styles.container}>
        <AspectRatioContainer useCssVariableSizes>
          <div className="mobileOnly">
            <ImageWithFallback
              layout="fill"
              objectFit="cover"
              src={`${referralsFolder}/cancellation-hero-mobile.svg`}
            />
          </div>

          <div className="desktopOnly">
            <ImageWithFallback
              layout="fill"
              objectFit="cover"
              src={`${referralsFolder}/cancellation-hero-desktop.svg`}
            />
          </div>
        </AspectRatioContainer>
        <div className={styles.inviteeSlotsOverlay}>
          {/* Invitees for referrer */}

          {inviteeSlots.map(({ index, player }) => {
            const isPlaceholder =
              !isReferrer && (index === '1' || index === '3');
            const isCurrentUser = !isReferrer && index === '2';
            return (
              <InviteeSlot
                key={index}
                index={index}
                player={isPlaceholder ? null : player}
                avatarUrl={
                  isPlaceholder
                    ? randomMascotUrls[index as '1' | '3']
                    : isCurrentUser
                      ? (currentUserAvatarUrl ?? NEW_MASCOTS[1])
                      : undefined
                }
                placeholder={isPlaceholder}
                currentUser={isCurrentUser}
                tailPosition={
                  index === '3' && isMobile ? 'left-bottom' : 'left-corner'
                }
              />
            );
          })}

          {/* Referrer or current user */}
          <div className={clsx(styles.avatarOnRubberRingContainer)}>
            <AvatarOnRubberRing
              avatarUrl={
                isReferrer
                  ? NEW_MASCOTS[1]
                  : (referrerAvatarUrl ?? NEW_MASCOTS[1])
              }
              color={RUBBER_RING_COLOR}
            />
          </div>
          <div className={clsx(styles.avatarSpeechBubbleContainer)}>
            <AvatarSpeechBubble backgroundColour={SPEECH_BUBBLE_COLOR}>
              <p className={styles.avatarSpeechBubbleText}>{MESSAGE}</p>
            </AvatarSpeechBubble>
          </div>
        </div>
      </div>
    </CancellationFlowStep>
  );
};
